import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { VapiConfiguration } from './v-api-configuration';
import { VERSION_PLACEHOLDER } from './constants';

@Injectable()
export class BaseService {
    protected config = inject(VapiConfiguration);
    protected http = inject(HttpClient);

    private _baseUrl?: string;
    private _entrypoint?: string;
    private _version?: string;

    /**
     * Returns the root url for all operations in this service. If not set directly in this
     * service, will fallback to `VapiConfiguration.baseUrl`.
     */
    get baseUrl(): string {
        return this._baseUrl || this.config.baseUrl;
    }

    /**
     * Sets the root URL for API operations in this service.
     */
    set baseUrl(baseUrl: string) {
        this._baseUrl = baseUrl;
    }

    /**
     * Returns the entrypoint for all operations in this service. If not set directly in this
     * service, will fallback to `VapiConfiguration.entrypoint`.
     */
    get entrypoint(): string {
        return this._entrypoint || this.config.entrypoint;
    }

    /**
     * Sets the entrypoint for API operations in this service.
     */
    set entrypoint(entrypoint: string) {
        this._entrypoint = entrypoint;
    }

    /**
     * Returns the version for all operations in this service. If not set directly in this
     * service, will fallback to `VapiConfiguration.version`.
     */
    get version(): string {
        return this._version || this.config.version;
    }

    /**
     * Sets the version for API operations in this service.
     */
    set version(version: string) {
        this._version = version;
    }

    getRootUrl(version: string = this.version): string {
        return this.baseUrl + this.entrypoint.replace(VERSION_PLACEHOLDER, version);
    }

    /**
     * Helper to build query params object for HttpClient from a plain object.
     * Filters out undefined/null values and keeps primitives/arrays as-is.
     */
    protected buildParams(
        params?: Record<string, any>
    ): HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> {
        if (!params) {
            return {};
        }

        const queryParams: Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> = {};

        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) {
                continue;
            }

            if (Array.isArray(value)) {
                queryParams[key] = value as ReadonlyArray<string | number | boolean>;
            } else {
                queryParams[key] = value as string | number | boolean;
            }
        }

        return queryParams;
    }
}
